<template>
  <div class="agenda">
    <header class="view-header">
      <div class="view-header__text">
        <h1 class="view-header__title">Agenda</h1>
        <p class="view-header__sub">Propietarios, inquilinos, garantes e interesados.</p>
      </div>

      <div class="agenda__create">
        <CreateModal ref="modal" text="Nuevo contacto" color="accent">
          <New @created="reload" @close="$refs.modal.dialog = false" />
        </CreateModal>
      </div>
    </header>

    <!-- filters -->
    <div class="agenda__filterbar">
      <div class="agenda__search">
        <hero-icon :icon="SearchIcon" :size="17" />
        <input
          v-model="search"
          type="text"
          :placeholder="$t('contact.list.filters.search')"
        />
      </div>

      <button
        type="button"
        class="agenda__filterbtn"
        :class="{ 'agenda__filterbtn--on': filtersOpen }"
        @click="filtersOpen = !filtersOpen"
        >
        <hero-icon :icon="FilterIcon" :size="17" />
        <span>Filtros</span>
        <span v-if="filters.roles.length" class="agenda__filterbadge">{{ filters.roles.length }}</span>
      </button>
    </div>

    <div v-if="filtersOpen" class="agenda__filterpanel">
      <div class="agenda__filterlabel">{{ $t('contact.list.filters.role') }}</div>
      <div class="agenda__chips">
        <button
          v-for="r in roles"
          :key="r.value"
          type="button"
          class="agenda__chip"
          :class="{ 'agenda__chip--on': filters.roles.includes(r.value) }"
          @click="toggleRole(r.value)"
          >{{ r.text }}</button>
      </div>
    </div>

    <List ref="list" />

    <v-btn
      v-if="$vuetify.breakpoint.smAndDown"
      fab
      fixed
      bottom
      right
      color="accent"
      class="agenda__fab"
      aria-label="Nuevo contacto"
      @click="$refs.modal.dialog = true"
      >
      <hero-icon :icon="PlusIcon" :size="24" />
    </v-btn>
  </div>
</template>

<script>
import List from '@/components/directories/List'
import New from '@/components/directories/New'
import CreateModal from '@/components/modals/Default'
import HeroIcon from '@/components/shared/HeroIcon'
import SearchIcon from 'heroicons/24/outline/magnifying-glass.svg'
import FilterIcon from 'heroicons/24/outline/funnel.svg'
import PlusIcon from 'heroicons/24/outline/plus.svg'

export default {
  data () {
    return {
      SearchIcon,
      FilterIcon,
      PlusIcon,
      filtersOpen: false,
      search: "",
      searchTimer: null,
      filters: {
        roles: []
      },
      roles: [
        { text: this.$t('roles.owner'), value: 'owner' },
        { text: this.$t('roles.tenant'), value: 'tenant' },
        { text: this.$t('roles.guarantor'), value: 'guarantor' },
        { text: this.$t('roles.seller'), value: 'seller' },
        { text: this.$t('roles.buyer'), value: 'buyer' },
        { text: this.$t('roles.contact'), value: 'contact' },
        { text: this.$t('roles.gendev'), value: 'gendev' }
      ]
    }
  },

  watch: {
    // debounce so we fire one query when typing settles, not per keystroke
    search () {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.$refs.list.fetchContacts('cache-first', this.search, this.filters)
      }, 300)
    },

    'filters.roles': {
      handler () {
        this.$refs.list.fetchContacts('cache-first', this.search, this.filters)
      },
      deep: true
    }
  },

  components: {
    List,
    New,
    CreateModal,
    HeroIcon
  },

  beforeDestroy () {
    clearTimeout(this.searchTimer)
  },

  methods: {
    toggleRole (value) {
      const i = this.filters.roles.indexOf(value)
      if (i >= 0) this.filters.roles.splice(i, 1)
      else this.filters.roles.push(value)
    },

    reload (user) {
      this.$refs.modal.dialog = false
      this.$refs.list.fetchContacts('no-cache', this.search, this.filters)
      if (user && user.id) {
        this.$nextTick(() => {
          if (this.$refs.list.$refs.detail) this.$refs.list.$refs.detail.open(user)
        })
      }
    }
  }
}
</script>

<style scoped>
/* layout (centering/cap/padding) comes from the global .app-content in App.vue */

.view-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}
.view-header__title {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  color: #1a1814;
  margin: 0;
}
.view-header__sub {
  font-size: 13.5px;
  color: #756f64;
  margin: 5px 0 0;
}

/* the create-contact CTA button (from the generic Modal) -> warm */
.agenda__create >>> .v-btn {
  border-radius: 10px;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
  box-shadow: none;
}

/* filters */
.agenda__filterbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 18px 0 12px;
}
.agenda__search {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 10px;
  padding: 0 12px;
  height: 42px;
}
.agenda__search .hero-icon { color: #a39a8c; flex: none; }
.agenda__search input {
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  font-size: 14px;
  color: #1a1814;
}
.agenda__search input::placeholder { color: #a39a8c; }

.agenda__filterbtn {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 10px;
  padding: 0 16px;
  height: 42px;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 600;
  color: #1a1814;
  transition: border-color 0.15s ease;
}
.agenda__filterbtn:hover,
.agenda__filterbtn--on { border-color: #dcd4c6; }
.agenda__filterbtn .hero-icon { color: #6a645a; }
.agenda__filterbadge {
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #b52118;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}

.agenda__filterpanel {
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 12px;
  padding: 16px 18px;
  margin: 0 0 12px;
}
.agenda__filterlabel {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
  margin-bottom: 9px;
}
.agenda__chips { display: flex; flex-wrap: wrap; gap: 8px; }
.agenda__chip {
  border-radius: 999px;
  border: 1px solid #ece7de;
  background: #fff;
  cursor: pointer;
  font-weight: 600;
  font-size: 12.5px;
  color: #56504a;
  padding: 8px 14px;
  transition: all 0.15s ease;
}
.agenda__chip:hover { border-color: #dcd4c6; }
.agenda__chip--on { background: #1a1814; color: #fff; border-color: #1a1814; }

.agenda__fab { z-index: 6; }
.agenda__fab .hero-icon { color: #fff; }

/* hide the header CTA exactly where the FAB (smAndDown) takes over */
@media (max-width: 959.98px) {
  .agenda__create { display: none; }
}
</style>
